import React from 'react';
import Notification from 'rc-notification';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faInfoCircle,
  faCheckCircle,
  faExclamationCircle,
  faExclamationTriangle,
  faTimesCircle,
} from '@fortawesome/pro-solid-svg-icons';
import { faTimes } from '@fortawesome/pro-light-svg-icons';
import type { NotificationInstance } from 'rc-notification/es/Notification';

import './Toast.scss';

/** An alert component. */

const close = () => <FontAwesomeIcon icon={faTimes} size="lg" />;

let notification: NotificationInstance;

Notification.newInstance(
  {
    closeIcon: close(),
    maxCount: 1,
  },
  (n: NotificationInstance) => {
    notification = n;
  }
);

const getToastOptions = (persistent?: boolean) => ({
  duration: persistent ? 0 : 7,
  closable: true,
  style: {},
  maxCount: 1,
});

const icon: Record<string, React.ReactElement> = {
  info: <FontAwesomeIcon icon={faInfoCircle} />,
  success: <FontAwesomeIcon icon={faCheckCircle} />,
  warning: <FontAwesomeIcon icon={faExclamationCircle} />,
  danger: <FontAwesomeIcon icon={faExclamationTriangle} />,
  dangerAlternative: <FontAwesomeIcon icon={faTimesCircle} />,
};

interface AlertContentProps {
  /** Alert details */
  msg: string | React.ReactElement;
  /** Alert headlines */
  title?: string;
  /** Color of the alert */
  color: 'info' | 'success' | 'warning' | 'danger';
  alternativeIcon?: boolean;
}

const AlertContent: React.FC<AlertContentProps> = ({
  msg,
  title = '',
  color,
  alternativeIcon,
}) => (
  <>
    <div className="alert-icon">
      {alternativeIcon ? icon[`${color}Alternative`] : icon[color]}
    </div>
    <div className="alert-content">
      {title && <strong>{title}</strong>}
      {typeof msg === 'string' ? <p data-testid="alertContent">{msg}</p> : msg}
    </div>
  </>
);

interface ToastOptions {
  alternativeIcon?: boolean;
  persistent?: boolean;
}

export { AlertContent };

export default {
  error: (
    msg: string | React.ReactElement,
    title: string = 'Error',
    options: ToastOptions = {}
  ) =>
    notification.notice({
      content: (
        <AlertContent
          color="danger"
          msg={msg}
          title={title}
          alternativeIcon={options.alternativeIcon}
        />
      ),
      className: 'alert alert-danger',
      ...getToastOptions(options.persistent),
    }),
  info: (
    msg: string | React.ReactElement,
    title?: string,
    options: ToastOptions = {}
  ) =>
    notification.notice({
      content: <AlertContent color="info" msg={msg} title={title} />,
      className: 'alert alert-info',
      ...getToastOptions(options.persistent),
    }),
  success: (
    msg: string | React.ReactElement,
    title: string = 'Success',
    options: ToastOptions = {}
  ) =>
    notification.notice({
      content: <AlertContent color="success" msg={msg} title={title} />,
      className: 'alert alert-success',
      ...getToastOptions(options.persistent),
    }),
  warning: (
    msg: string | React.ReactElement,
    title?: string,
    options: ToastOptions = {}
  ) =>
    notification.notice({
      content: <AlertContent color="warning" msg={msg} title={title} />,
      className: 'alert alert-warning',
      ...getToastOptions(options.persistent),
    }),
};
